import { useNavigate } from 'react-router-dom'
import { useApi, useAction } from '../lib/hooks'
import { api } from '../lib/api'
import { against, clock, money, num, tonnes } from '../lib/format'
import {
  AlertCard, Delta, Empty, ErrorNote, Kpi, Loading, Panel, Pill, StatMini,
} from '../ui/kit'

interface Dashboard {
  date: string
  collected: {
    kg: number; value: number; lots: number; plannedKg: number; routes: number
    fillPct: number | null; avgVariancePct: number | null; unexplainedLots: number
  }
  lastMile: {
    vanDays: number; activeVans: number; drops: number; deliveredKg: number; rejectedKg: number
    dropsPerVanDay: number | null; kgPerVanDay: number | null; avgFillPct: number | null
    targetDrops: number; targetKgPerVanDay: number
  }
  primaryHaul: { avgFillPct: number | null; targetFillPct: number }
  service: { orderedKg: number; acceptedKg: number; orders: number; fillRatePct: number | null }
  shrink: { purchasedKg: number; atDepotKg: number; shrinkPct: number | null; targetPct: number }
  payments: {
    count: number; settled: number; failed: number; total: number
    settledAmount: number; settledPct: number | null; cutoff: string
  }
  exceptions: { critical: number; warn: number; total: number; valueAtRisk: number }
  offers: {
    open: number; farmers: number; kg: number; pastReady: number; oldest: string | null
  }
}

interface RouteLive {
  id: string; code: string; status: string; zoneName: string; agentName: string | null
  stopsPlanned: number; stopsDone: number; plannedKg: number; collectedKg: number
  collectedValue: number; fillPct: number | null; avgVariancePct: number | null
  lotsUnexplained: number
}

interface OpsException {
  id: string; code: string; kind: string; severity: 'critical' | 'warn' | 'info'
  title: string; detail: string | null; valueAtRisk: number | null
  status: string; openedAt: string; slaBreached: boolean
}

/** What each kind of problem is called on screen, rather than its internal name. */
const KIND_LABEL: Record<string, string> = {
  rejection: 'buyer sent it back',
  payment_retry: 'payment failed',
  scale_qc: 'scale problem',
  variance: 'weight missing',
  sla_breach: 'overdue',
}

export function ControlRoom() {
  const nav = useNavigate()
  const dash = useApi<Dashboard>('/ops/dashboard')
  const routes = useApi<RouteLive[]>('/routes/live')
  const exceptions = useApi<OpsException[]>('/ops/exceptions')

  const resolve = useAction(async (id: string, resolution: string) => {
    await api.post(`/ops/exceptions/${id}/resolve`, { resolution })
    exceptions.reload()
    dash.reload()
  })

  if (dash.error) return <div className="page"><ErrorNote error={dash.error} onRetry={dash.reload} /></div>

  const d = dash.data

  return (
    <main className="page">
      <div className="page-head spread">
        <div>
          <h1>Today</h1>
          <p>
            What you bought, what it cost you, what went out, and anything that needs you now.
          </p>
        </div>
        {d && (
          <Pill tone={d.exceptions.critical > 0 ? 'crit' : d.offers.open > 0 ? 'warn' : 'ok'}>
            {d.exceptions.critical > 0
              ? `${d.exceptions.critical} urgent`
              : d.offers.open > 0
                ? `${d.offers.open} to answer`
                : 'All clear'}
          </Pill>
        )}
      </div>

      {!d ? (
        <Panel><Loading rows={3} /></Panel>
      ) : (
        <div className="stack">
          <div className="kpi-grid">
            <Kpi
              label="Bought today"
              value={tonnes(d.collected.kg)}
              sub={<>out of {tonnes(d.collected.plannedKg)} planned, in {d.collected.lots} collections</>}
              meterPct={d.collected.fillPct}
              tone={against(d.collected.fillPct, 95)}
            />
            <Kpi
              label="Deliveries per van"
              value={d.lastMile.dropsPerVanDay ?? '—'}
              sub={<>aim for {d.lastMile.targetDrops} or more — fewer stops means each one costs more</>}
              meterPct={
                d.lastMile.dropsPerVanDay === null
                  ? 0
                  : (d.lastMile.dropsPerVanDay / d.lastMile.targetDrops) * 100
              }
              tone={against(d.lastMile.dropsPerVanDay, d.lastMile.targetDrops)}
            />
            <Kpi
              label="How full the lorries go"
              value={d.primaryHaul.avgFillPct ?? '—'}
              unit="%"
              sub={<>aim for {d.primaryHaul.targetFillPct}% or more — half-empty trips cost the same</>}
              meterPct={d.primaryHaul.avgFillPct}
              tone={against(d.primaryHaul.avgFillPct, d.primaryHaul.targetFillPct)}
            />
            <Kpi
              label="Weight lost"
              value={d.shrink.shrinkPct ?? '—'}
              unit="%"
              sub={<>keep under {d.shrink.targetPct}% — weighed at the farm, weighed again at the store</>}
              meterPct={
                d.shrink.shrinkPct === null ? 0 : (d.shrink.shrinkPct / d.shrink.targetPct) * 100
              }
              tone={against(d.shrink.shrinkPct, d.shrink.targetPct, false)}
            />
            <Kpi
              label="Orders filled in full"
              value={d.service.fillRatePct ?? '—'}
              unit="%"
              sub={<>{d.service.orders} order{d.service.orders === 1 ? '' : 's'} today — short deliveries lose buyers</>}
              meterPct={d.service.fillRatePct}
              tone={against(d.service.fillRatePct, 95)}
            />
            <Kpi
              label="Farmers waiting for a reply"
              value={d.offers.open}
              sub={
                d.offers.open > 0
                  ? <>{num(d.offers.kg, 0)} kg offered by {d.offers.farmers} farmer{d.offers.farmers === 1 ? '' : 's'}</>
                  : <>you have answered everyone</>
              }
              tone={d.offers.pastReady > 0 ? 'crit' : d.offers.open > 0 ? 'warn' : 'ok'}
            />
            <Kpi
              label="Missing weight, no reason given"
              value={d.collected.unexplainedLots}
              sub={
                d.collected.unexplainedLots > 0
                  ? <>somebody has to say what happened to these</>
                  : <>every kilo is accounted for</>
              }
              tone={d.collected.unexplainedLots > 0 ? 'crit' : 'ok'}
            />
          </div>

          <div className="split">
            <div className="stack">
              <Panel
                title="Collection routes — live"
                meta="weighed at the farm, again at the store, again at the buyer"
                footer={
                  <>
                    <StatMini label="Farmers paid" value={`${d.payments.settled} / ${d.payments.count}`}
                      note={`due by ${clock(d.payments.cutoff)}`} />
                    <StatMini label="Being paid out" value={money(d.payments.total, { compact: true })}
                      note={`${money(d.payments.settledAmount, { compact: true })} settled`} />
                    <StatMini label="Payments that failed" value={d.payments.failed}
                      note={d.payments.failed > 0 ? 'will be tried again' : 'none'} />
                    <StatMini label="Vans out now" value={`${d.lastMile.activeVans} / ${d.lastMile.vanDays}`} />
                  </>
                }
              >
                {routes.loading && <Loading />}
                {routes.data && routes.data.length === 0 && (
                  <Empty title="No routes planned for today">
                    Plan a route to give a field agent a list of farmers, a price board and a payout budget.
                  </Empty>
                )}
                {routes.data && routes.data.length > 0 && (
                  <div className="table-wrap">
                    <table className="data">
                      <thead>
                        <tr>
                          <th>Route</th>
                          <th>Area</th>
                          <th>Agent</th>
                          <th className="n">Stops</th>
                          <th className="n">Collected</th>
                          <th className="n">Planned</th>
                          <th className="n">How full</th>
                          <th className="n">Difference</th>
                        </tr>
                      </thead>
                      <tbody>
                        {routes.data.map((r) => (
                          <tr key={r.id} className={r.lotsUnexplained > 0 ? 'attention' : ''}>
                            <td className="strong code">{r.code}</td>
                            <td>{r.zoneName}</td>
                            <td className="truncate">{r.agentName ?? '—'}</td>
                            <td className="n">{r.stopsDone} / {r.stopsPlanned}</td>
                            <td className="n">{num(r.collectedKg)} kg</td>
                            <td className="n faint">{num(r.plannedKg)} kg</td>
                            <td className="n">{r.fillPct === null ? '—' : `${r.fillPct}%`}</td>
                            <td className="n">
                              <Delta value={r.avgVariancePct} goodDirection="zero" decimals={2} />
                              {r.lotsUnexplained > 0 && (
                                <> <Pill tone="crit" dot={false}>{r.lotsUnexplained} open</Pill></>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Panel>

              <Panel
                title="Last mile"
                meta={`${num(d.lastMile.deliveredKg)} kg delivered · ${d.lastMile.drops} drops`}
              >
                <div className="panel-body mini-row">
                  <StatMini
                    label="Kg per van"
                    value={num(d.lastMile.kgPerVanDay)}
                    note={`target ${num(d.lastMile.targetKgPerVanDay)}`}
                  />
                  <StatMini label="Avg how full" value={d.lastMile.avgFillPct === null ? '—' : `${d.lastMile.avgFillPct}%`} />
                  <StatMini label="Rejected" value={`${num(d.lastMile.rejectedKg)} kg`}
                    note={d.lastMile.rejectedKg > 0 ? 'coming back to the store' : 'none today'} />
                  <StatMini label="Accepted" value={`${num(d.service.acceptedKg)} kg`}
                    note={`of ${num(d.service.orderedKg)} ordered`} />
                </div>
              </Panel>
            </div>

            <Panel
              title="Needs attention"
              meta={
                d.exceptions.total > 0
                  ? <span className="crit-t">{d.exceptions.total} open · {money(d.exceptions.valueAtRisk, { compact: true })} at risk</span>
                  : d.offers.open > 0
                    ? <span className="warn-t">{d.offers.open} offer{d.offers.open === 1 ? '' : 's'} to answer</span>
                    : 'nothing open'
              }
            >
              {d.offers.open > 0 && (
                <AlertCard
                  tone={d.offers.pastReady > 0 ? 'crit' : 'warn'}
                  tag={`farmer offers${d.offers.pastReady > 0 ? ' · already past the date' : ''}`}
                  title={`${d.offers.open} farmer${d.offers.open === 1 ? ' is' : 's are'} waiting to hear back`}
                  actions={
                    <button className="btn" onClick={() => nav('/console/offers')}>See the offers</button>
                  }
                >
                  {num(d.offers.kg, 0)} kg from {d.offers.farmers} farmer
                  {d.offers.farmers === 1 ? '' : 's'}
                  {d.offers.pastReady > 0 && <>, {d.offers.pastReady} already past the day it was ready</>}
                  . A farmer who posts and hears nothing stops posting.
                </AlertCard>
              )}
              {exceptions.loading && <Loading rows={3} />}
              {exceptions.data && exceptions.data.length === 0 && d.offers.open === 0 && (
                <Empty title="Nothing needs you">
                  Every collection adds up, every farmer has been paid, and every delivery was
                  accepted.
                </Empty>
              )}
              {exceptions.data?.map((e) => (
                <AlertCard
                  key={e.id}
                  tone={e.severity === 'critical' ? 'crit' : e.severity === 'warn' ? 'warn' : 'info'}
                  tag={`${e.code} · ${KIND_LABEL[e.kind] ?? e.kind.replace(/_/g, ' ')}${e.slaBreached ? ' · overdue' : ''}`}
                  title={e.title}
                  actions={
                    <>
                      {e.kind === 'rejection' && (
                        <button className="btn ghost" onClick={() => nav('/console/logistics')}>
                          Open
                        </button>
                      )}
                      <button
                        className="btn quiet"
                        disabled={resolve.busy}
                        onClick={() => {
                          const note = window.prompt(
                            'What was done? An exception closed without a note teaches nobody anything.',
                          )
                          if (note?.trim()) void resolve.run(e.id, note.trim())
                        }}
                      >
                        Resolve
                      </button>
                    </>
                  }
                >
                  {e.detail}
                  {e.valueAtRisk ? <> <b>{money(e.valueAtRisk)} at risk.</b></> : null}
                </AlertCard>
              ))}
            </Panel>
          </div>
        </div>
      )}
    </main>
  )
}
